import React from "react";
import { Doc, Block, BlockType, uid, makeBlock, PALETTE } from "./types";

const h = React.createElement;

export interface BuilderProps {
  doc: Doc;
  variant: "newsletter" | "article";
  mode: "edit" | "read";
  theme: "dark" | "light";
  onChange: (doc: Doc) => void;
  onUploadImage?: (file: File) => Promise<string>;
}

export default function Builder({ doc, variant, mode, theme, onChange, onUploadImage }: BuilderProps) {
  const edit = mode === "edit";
  const head = doc.head;
  const blocks = doc.blocks;

  const commit = () => onChange({ head: { ...head }, blocks: blocks.slice() });

  // ----- block ops -----
  const move = (i: number, d: number) => {
    const j = i + d;
    if (j < 0 || j >= blocks.length) return;
    const n = blocks.slice();
    [n[i], n[j]] = [n[j], n[i]];
    onChange({ head, blocks: n });
  };
  const del = (i: number) => {
    const n = blocks.slice();
    n.splice(i, 1);
    onChange({ head, blocks: n });
  };
  const dup = (i: number) => {
    const n = blocks.slice();
    n.splice(i + 1, 0, JSON.parse(JSON.stringify({ ...n[i], id: uid() })));
    onChange({ head, blocks: n });
  };
  const add = (type: BlockType, data?: any) => {
    const n = blocks.slice();
    n.push(makeBlock(type, data));
    onChange({ head, blocks: n });
  };

  const pickImage = (d: any, file: File | undefined, field = "imgSrc") => {
    if (!file) return;
    if (onUploadImage) {
      onUploadImage(file).then((url) => {
        d[field] = url;
        commit();
      });
    } else {
      const r = new FileReader();
      r.onload = () => {
        d[field] = r.result;
        commit();
      };
      r.readAsDataURL(file);
    }
  };

  // ----- editable helpers -----
  const ed = (obj: any, field: string | number, tag: string, style: any, key?: string) =>
    h(tag as any, {
      key,
      className: "ed",
      contentEditable: edit,
      suppressContentEditableWarning: true,
      spellCheck: false,
      style,
      onBlur: edit
        ? (e: any) => {
            obj[field] = e.currentTarget.innerHTML;
            commit();
          }
        : undefined,
      dangerouslySetInnerHTML: { __html: (obj[field] as string) || "" },
    });

  const paras = (obj: any, pstyle: any) => {
    const list: string[] = obj.paras || [];
    const nodes: any[] = list.map((p, i) =>
      h("p", { key: i, style: { position: "relative", margin: "0 0 13px" } }, [
        edit
          ? h(
              "button",
              {
                key: "d",
                className: "pdel",
                title: "Delete paragraph",
                onClick: () => {
                  obj.paras.splice(i, 1);
                  commit();
                },
              },
              "×",
            )
          : null,
        h("span", {
          key: "t",
          className: "ed",
          contentEditable: edit,
          suppressContentEditableWarning: true,
          spellCheck: false,
          style: pstyle,
          onBlur: edit
            ? (e: any) => {
                obj.paras[i] = e.currentTarget.innerHTML;
                commit();
              }
            : undefined,
          dangerouslySetInnerHTML: { __html: p },
        }),
      ]),
    );
    if (edit)
      nodes.push(
        h(
          "button",
          {
            key: "add",
            className: "miniadd",
            onClick: () => {
              obj.paras.push("New paragraph.");
              commit();
            },
          },
          "+ paragraph",
        ),
      );
    return nodes;
  };

  const divider = (obj: any) =>
    h("div", { style: { display: "flex", alignItems: "baseline", gap: "14px", marginBottom: "6px" } }, [
      ed(
        obj,
        "kicker",
        "div",
        {
          fontFamily: "'Zilla Slab',sans-serif",
          fontWeight: 700,
          fontSize: "13px",
          textTransform: "uppercase",
          letterSpacing: ".18em",
          color: "var(--accent)",
          whiteSpace: "nowrap",
        },
        "k",
      ),
      h("div", { key: "r", style: { flex: 1, height: "1px", background: "var(--rule2)" } }),
    ]);

  const stripe = "repeating-linear-gradient(45deg,var(--paper3),var(--paper3) 9px,var(--paper),var(--paper) 18px)";

  // Editable caption line for an image (placeholder hint in edit mode; hidden in
  // read mode when empty).
  const captionEl = (d: any, field: string) =>
    edit || d[field]
      ? h("figcaption", {
          key: "cap",
          className: "ed vox-cap",
          contentEditable: edit,
          suppressContentEditableWarning: true,
          spellCheck: false,
          "data-ph": "Add a caption…",
          style: { font: "500 11.5px/1.5 'IBM Plex Mono',monospace", color: "var(--muted)", marginTop: "8px", textAlign: "center" },
          onBlur: edit
            ? (e: any) => {
                d[field] = e.currentTarget.innerHTML;
                commit();
              }
            : undefined,
          dangerouslySetInnerHTML: { __html: d[field] || "" },
        })
      : null;

  // A replaceable image area: shows the uploaded image (with caption + swap/remove
  // in edit mode) or a striped placeholder with an editable label + upload button.
  const imageArea = (d: any, srcField: string, labelField: string, height: string, labelStyle: any, captionField = "imgCaption") => {
    if (d[srcField]) {
      return h("figure", { style: { margin: 0 } }, [
        h("img", { key: "img", src: d[srcField], alt: "", style: { width: "100%", display: "block", border: "1px solid var(--rule2)" } }),
        captionEl(d, captionField),
        edit
          ? h("div", { key: "ct", style: { display: "flex", gap: "8px", marginTop: "8px", justifyContent: "center" } }, [
              h("label", { key: "sw", className: "miniadd", style: { cursor: "pointer" } }, [
                "swap image",
                h("input", { key: "i", type: "file", accept: "image/*", style: { display: "none" }, onChange: (e: any) => pickImage(d, e.target.files[0], srcField) }),
              ]),
              h("button", { key: "rm", className: "miniadd", onClick: () => { delete d[srcField]; commit(); } }, "remove"),
            ])
          : null,
      ]);
    }
    return h(
      "div",
      { style: { height, background: stripe, border: "1px solid var(--rule2)", display: "flex", alignItems: "center", justifyContent: "center", flexDirection: "column", gap: "10px", textAlign: "center" } },
      [
        ed(d, labelField, "span", labelStyle, "l"),
        edit
          ? h("label", { key: "up", className: "miniadd", style: { cursor: "pointer" } }, [
              "upload image",
              h("input", { key: "i", type: "file", accept: "image/*", style: { display: "none" }, onChange: (e: any) => pickImage(d, e.target.files[0], srcField) }),
            ])
          : null,
      ],
    );
  };

  const labelStyle = { font: "500 11px 'IBM Plex Mono',monospace", color: "var(--muted)" };

  const renderBlock = (b: Block) => {
    const d = b.data;
    switch (b.type) {
      case "heading":
        return h("div", {}, [
          divider(d),
          ed(
            d,
            "title",
            "h2",
            {
              fontFamily: d.serif ? "'Cinzel',serif" : "'Zilla Slab',sans-serif",
              fontWeight: 700,
              fontSize: d.serif ? "30px" : "34px",
              lineHeight: 1.02,
              margin: "0 0 4px",
              color: "var(--ink)",
            },
            "t",
          ),
          ed(d, "sub", "div", { font: "italic 400 15px/1.5 'Spectral',serif", color: "var(--muted)" }, "s"),
          edit
            ? h(
                "label",
                {
                  key: "sf",
                  style: {
                    display: "inline-flex",
                    gap: "6px",
                    alignItems: "center",
                    marginTop: "8px",
                    font: "600 10px 'IBM Plex Mono',monospace",
                    color: "var(--muted)",
                    textTransform: "uppercase",
                    letterSpacing: ".08em",
                  },
                },
                [
                  h("input", {
                    key: "c",
                    type: "checkbox",
                    checked: !!d.serif,
                    onChange: (e: any) => {
                      d.serif = e.target.checked;
                      commit();
                    },
                  }),
                  "Cinzel title",
                ],
              )
            : null,
        ]);

      case "columns":
      case "cards": {
        const cards = b.type === "cards";
        const grid = {
          display: "grid",
          gridTemplateColumns: `repeat(${d.cols}, 1fr)`,
          gap: cards ? "16px" : "26px",
        };
        const items = d.items.map((it: any, i: number) =>
          h(
            "div",
            {
              key: i,
              style: cards
                ? { border: "1px solid var(--rule2)", padding: "16px 18px", position: "relative" }
                : { borderTop: "2px solid var(--accent)", paddingTop: "12px", position: "relative" },
            },
            [
              edit
                ? h(
                    "button",
                    {
                      key: "x",
                      className: "pdel",
                      style: { left: "auto", right: "4px", top: "4px", opacity: 1 },
                      onClick: () => {
                        d.items.splice(i, 1);
                        commit();
                      },
                    },
                    "×",
                  )
                : null,
              ed(
                it,
                "kicker",
                "div",
                {
                  font: "700 10px 'IBM Plex Mono',monospace",
                  textTransform: "uppercase",
                  letterSpacing: ".13em",
                  color: "var(--gold)",
                  marginBottom: "7px",
                },
                "k",
              ),
              ed(
                it,
                "head",
                "h3",
                {
                  fontFamily: "'Zilla Slab',sans-serif",
                  fontWeight: 700,
                  fontSize: cards ? "17px" : "19px",
                  lineHeight: 1.1,
                  margin: "0 0 7px",
                  color: "var(--ink)",
                },
                "h",
              ),
              ed(it, "body", "p", { fontSize: "13.5px", lineHeight: 1.58, margin: 0, color: "var(--ink2)" }, "b"),
              cards && (it.tag || edit)
                ? h(
                    "div",
                    { key: "tg", style: { marginTop: "10px" } },
                    ed(
                      it,
                      "tag",
                      "span",
                      {
                        display: "inline-block",
                        font: "700 10px 'IBM Plex Mono',monospace",
                        textTransform: "uppercase",
                        letterSpacing: ".1em",
                        color: "var(--gold)",
                        border: "1px solid var(--gold)",
                        padding: "4px 9px",
                        borderRadius: "2px",
                      },
                      "t",
                    ),
                  )
                : null,
            ],
          ),
        );
        return h("div", {}, [
          h("div", { key: "g", style: grid }, items),
          edit
            ? h("div", { key: "a", style: { marginTop: "12px", display: "flex", gap: "8px" } }, [
                h(
                  "button",
                  {
                    key: "+",
                    className: "miniadd",
                    onClick: () => {
                      d.items.push(
                        cards
                          ? { kicker: "Category", head: "Card title", body: "Short card body.", tag: "" }
                          : { kicker: "Topic", head: "Headline", body: "Body copy." },
                      );
                      commit();
                    },
                  },
                  cards ? "+ card" : "+ column",
                ),
                h(
                  "button",
                  {
                    key: "c",
                    className: "miniadd",
                    onClick: () => {
                      d.cols = d.cols >= 3 ? 1 : d.cols + 1;
                      commit();
                    },
                  },
                  `columns: ${d.cols}`,
                ),
              ])
            : null,
        ]);
      }

      case "article":
        return h("div", {}, [
          h("div", { key: "im", style: { margin: "0 0 16px" } }, imageArea(d, "imgSrc", "imageLabel", "150px", labelStyle)),
          h(
            "div",
            { key: "bd", style: { fontSize: "15px", lineHeight: 1.7, color: "var(--ink2)", columnCount: d.cols, columnGap: "40px" } },
            paras(d, { fontSize: "15px", lineHeight: 1.7, color: "var(--ink2)" }),
          ),
        ]);

      case "editorial":
        return h("div", { style: { display: "grid", gridTemplateColumns: "1.7fr 1fr", gap: "34px", alignItems: "start" } }, [
          h("div", { key: "l" }, [
            ed(
              d,
              "author",
              "div",
              {
                font: "600 11px 'IBM Plex Mono',monospace",
                textTransform: "uppercase",
                letterSpacing: ".1em",
                color: "var(--muted)",
                marginBottom: "14px",
              },
              "au",
            ),
            h(
              "div",
              { key: "p", style: { fontSize: "15.5px", lineHeight: 1.68, color: "var(--ink2)" } },
              paras(d, { fontSize: "15.5px", lineHeight: 1.68, color: "var(--ink2)" }),
            ),
          ]),
          h("div", { key: "r" }, [
            h("div", { key: "im" }, imageArea(d, "imgSrc", "imageLabel", "190px", labelStyle)),
            h(
              "div",
              { key: "q", style: { borderLeft: "3px solid var(--accent)", padding: "10px 0 10px 16px", marginTop: "18px" } },
              ed(
                d,
                "quote",
                "div",
                { fontFamily: "'Spectral',serif", fontStyle: "italic", fontWeight: 500, fontSize: "19px", lineHeight: 1.3, color: "var(--ink)" },
                "qq",
              ),
            ),
          ]),
        ]);

      case "table": {
        const gt = `repeat(${d.cols.length}, 1fr)`;
        return h("div", {}, [
          h("div", { key: "t", style: { border: "1.5px solid var(--rule)", background: "var(--paper2)" } }, [
            h(
              "div",
              {
                key: "hd",
                style: {
                  display: "grid",
                  gridTemplateColumns: gt,
                  font: "700 10px 'IBM Plex Mono',monospace",
                  textTransform: "uppercase",
                  letterSpacing: ".1em",
                  color: "var(--muted)",
                  borderBottom: "1.5px solid var(--rule)",
                },
              },
              d.cols.map((_c: string, ci: number) =>
                h("div", { key: ci, style: { padding: "10px 14px" } }, ed(d.cols, ci, "span", {}, "c" + ci)),
              ),
            ),
            ...d.rows.map((row: string[], ri: number) =>
              h(
                "div",
                {
                  key: ri,
                  style: {
                    display: "grid",
                    gridTemplateColumns: gt,
                    fontSize: "13.5px",
                    borderBottom: ri < d.rows.length - 1 ? "1px solid var(--rule2)" : "none",
                    position: "relative",
                  },
                },
                [
                  ...row.map((_cell: string, ci: number) =>
                    h(
                      "div",
                      {
                        key: ci,
                        style: {
                          padding: "11px 14px",
                          color: ci === 0 ? "var(--ink)" : ci === row.length - 1 ? "var(--gold)" : "var(--ink2)",
                          fontWeight: ci === 0 || ci === row.length - 1 ? 600 : 400,
                        },
                      },
                      ed(row, ci, "span", {}, "c" + ci),
                    ),
                  ),
                  edit
                    ? h(
                        "button",
                        {
                          key: "x",
                          className: "pdel",
                          style: { left: "auto", right: "2px", top: "9px", opacity: 1 },
                          onClick: () => {
                            d.rows.splice(ri, 1);
                            commit();
                          },
                        },
                        "×",
                      )
                    : null,
                ],
              ),
            ),
          ]),
          edit
            ? h(
                "button",
                {
                  key: "a",
                  className: "miniadd",
                  style: { marginTop: "12px" },
                  onClick: () => {
                    d.rows.push(d.cols.map(() => "cell"));
                    commit();
                  },
                },
                "+ row",
              )
            : null,
        ]);
      }

      case "qa":
        return h("div", { style: { maxWidth: "720px" } }, [
          ...d.rows.map((r: any, i: number) =>
            h("div", { key: i, style: { display: "flex", gap: "14px", marginBottom: "13px", position: "relative" } }, [
              ed(r, "who", "div", { flex: "none", width: "56px", font: "700 12px 'IBM Plex Mono',monospace", color: r.a ? "var(--accent)" : "var(--gold)" }, "w"),
              ed(r, "text", "div", { fontSize: "15px", lineHeight: 1.66, color: r.a ? "var(--ink)" : "var(--ink2)" }, "t"),
              edit
                ? h(
                    "button",
                    {
                      key: "x",
                      className: "pdel",
                      style: { left: "auto", right: "-4px", top: 0, opacity: 1 },
                      onClick: () => {
                        d.rows.splice(i, 1);
                        commit();
                      },
                    },
                    "×",
                  )
                : null,
            ]),
          ),
          edit
            ? h("div", { key: "a", style: { display: "flex", gap: "8px", marginTop: "4px" } }, [
                h(
                  "button",
                  {
                    key: "q",
                    className: "miniadd",
                    onClick: () => {
                      d.rows.push({ who: "VOX", a: false, text: "Question." });
                      commit();
                    },
                  },
                  "+ question",
                ),
                h(
                  "button",
                  {
                    key: "ans",
                    className: "miniadd",
                    onClick: () => {
                      d.rows.push({ who: "FC", a: true, text: "Answer." });
                      commit();
                    },
                  },
                  "+ answer",
                ),
              ])
            : null,
        ]);

      case "panel":
        return h(
          "div",
          {
            style: {
              background: "var(--paper2)",
              border: "1.5px solid var(--rule)",
              borderLeft: "5px solid var(--gold)",
              padding: "24px 28px",
              display: "flex",
              gap: "24px",
              alignItems: "flex-start",
            },
          },
          [
            d.bee
              ? h("img", {
                  key: "b",
                  src: d.imgSrc || "/bee.png",
                  alt: "Panel",
                  style: { width: "130px", flex: "none", marginTop: "4px", objectFit: "contain" },
                })
              : null,
            h("div", { key: "c", style: { flex: 1 } }, [
              ed(d, "title", "h2", { fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: "25px", lineHeight: 1.05, margin: "0 0 4px", color: "var(--ink)" }, "t"),
              ed(d, "sub", "div", { font: "italic 400 14px/1.4 'Spectral',serif", color: "var(--muted)", marginBottom: "14px" }, "s"),
              h("div", { key: "p", style: { fontSize: "15px", lineHeight: 1.66, color: "var(--ink2)" } }, paras(d, { fontSize: "15px", lineHeight: 1.66, color: "var(--ink2)" })),
              d.callout || edit
                ? h(
                    "div",
                    { key: "co", style: { background: "var(--paper)", border: "1px dashed var(--rule)", padding: "14px 18px", marginTop: "12px", textAlign: "center" } },
                    ed(d, "callout", "div", { fontFamily: "'IBM Plex Mono',monospace", fontSize: "18px", color: "var(--accent)", letterSpacing: ".04em" }, "co"),
                  )
                : null,
              edit
                ? h("div", { key: "be", style: { display: "flex", gap: "10px", alignItems: "center", marginTop: "12px", flexWrap: "wrap" } }, [
                    h(
                      "label",
                      { key: "sh", style: { display: "inline-flex", gap: "6px", alignItems: "center", font: "600 10px 'IBM Plex Mono',monospace", color: "var(--muted)", textTransform: "uppercase" } },
                      [
                        h("input", {
                          key: "c",
                          type: "checkbox",
                          checked: !!d.bee,
                          onChange: (e: any) => {
                            d.bee = e.target.checked;
                            commit();
                          },
                        }),
                        "show image",
                      ],
                    ),
                    d.bee
                      ? h("label", { key: "sw", className: "miniadd", style: { cursor: "pointer" } }, [
                          "swap image",
                          h("input", {
                            key: "i",
                            type: "file",
                            accept: "image/*",
                            style: { display: "none" },
                            onChange: (e: any) => pickImage(d, e.target.files[0]),
                          }),
                        ])
                      : null,
                    d.bee && d.imgSrc
                      ? h(
                          "button",
                          {
                            key: "rs",
                            className: "miniadd",
                            onClick: () => {
                              delete d.imgSrc;
                              commit();
                            },
                          },
                          "reset to bee",
                        )
                      : null,
                  ])
                : null,
            ]),
          ],
        );

      case "tales":
        return h("div", { style: { maxWidth: "680px" } }, [
          ed(d, "title", "h2", { fontFamily: "'Cinzel',serif", fontStyle: "italic", fontWeight: 700, fontSize: "28px", lineHeight: 1.05, margin: "0 0 4px", color: "var(--ink)" }, "t"),
          ed(d, "sub", "div", { font: "600 11px 'IBM Plex Mono',monospace", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)", marginBottom: "18px" }, "s"),
          h("div", { key: "p", style: { fontSize: "15.5px", lineHeight: 1.74, color: "var(--ink2)", fontStyle: "italic" } }, paras(d, { fontSize: "15.5px", lineHeight: 1.74, color: "var(--ink2)", fontStyle: "italic" })),
          ed(d, "footer", "div", { fontSize: "13px", color: "var(--muted)", marginTop: "4px" }, "f"),
        ]);

      case "quote":
        return h("div", { style: { textAlign: "center", padding: "10px 40px" } }, [
          ed(d, "text", "div", { fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: "26px", lineHeight: 1.25, color: "var(--ink)" }, "t"),
          ed(d, "cite", "div", { font: "600 11px 'IBM Plex Mono',monospace", textTransform: "uppercase", letterSpacing: ".12em", color: "var(--gold)", marginTop: "14px" }, "c"),
        ]);

      case "image": {
        const caption = captionEl(d, "caption");
        return d.src
          ? h("figure", { style: { margin: 0 } }, [
              h("img", { key: "img", src: d.src, alt: d.label || "", style: { width: "100%", display: "block", border: "1px solid var(--rule2)" } }),
              caption,
              edit
                ? h("div", { key: "ct", style: { display: "flex", gap: "8px", marginTop: "8px" } }, [
                    h("label", { key: "sw", className: "miniadd", style: { cursor: "pointer" } }, [
                      "swap image",
                      h("input", { key: "i", type: "file", accept: "image/*", style: { display: "none" }, onChange: (e: any) => pickImage(d, e.target.files[0], "src") }),
                    ]),
                    h("button", { key: "rm", className: "miniadd", onClick: () => { delete d.src; commit(); } }, "remove"),
                  ])
                : null,
            ])
          : h(
              "div",
              {
                style: {
                  height: d.tall ? "320px" : "190px",
                  background: stripe,
                  border: "1px solid var(--rule2)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexDirection: "column",
                  gap: "10px",
                },
              },
              [
                ed(d, "label", "span", { font: "500 11px 'IBM Plex Mono',monospace", color: "var(--muted)" }, "l"),
                edit
                  ? h("div", { key: "ct", style: { display: "flex", gap: "8px" } }, [
                      h("label", { key: "up", className: "miniadd", style: { cursor: "pointer" } }, [
                        "upload image",
                        h("input", { key: "i", type: "file", accept: "image/*", style: { display: "none" }, onChange: (e: any) => pickImage(d, e.target.files[0], "src") }),
                      ]),
                      h("button", { key: "t", className: "miniadd", onClick: () => { d.tall = !d.tall; commit(); } }, d.tall ? "shorter" : "taller"),
                    ])
                  : null,
              ],
            );
      }

      default:
        return null;
    }
  };

  // ----- TOC -----
  const toc = () => {
    const items = blocks.filter((b) => b.type === "heading");
    if (!items.length) return null;
    return h("nav", { style: { borderTop: "1px solid var(--rule2)", borderBottom: "1px solid var(--rule2)", padding: "22px 56px", background: "var(--paper2)" } }, [
      h("div", { key: "h", style: { font: "700 11px 'IBM Plex Mono',monospace", textTransform: "uppercase", letterSpacing: ".18em", color: "var(--accent)", marginBottom: "14px" } }, "In This Issue"),
      h(
        "div",
        { key: "l", style: { display: "grid", gridTemplateColumns: "repeat(2,1fr)", gap: "4px 40px" } },
        items.map((b, i) =>
          h("a", { key: b.id, href: "#" + b.id, style: { display: "flex", justifyContent: "space-between", alignItems: "baseline", borderBottom: "1px dotted var(--rule2)", padding: "5px 0", color: "var(--ink)", fontSize: "15px" } }, [
            h("span", { key: "t", dangerouslySetInnerHTML: { __html: b.data.title } }),
            h("span", { key: "n", style: { font: "600 11px 'IBM Plex Mono',monospace", color: "var(--gold)", marginLeft: "12px" } }, String(i + 1).padStart(2, "0")),
          ]),
        ),
      ),
    ]);
  };

  // ----- masthead -----
  const masthead = () =>
    h("header", { style: { position: "relative", overflow: "hidden", padding: "40px 56px 26px" } }, [
      h("img", { key: "wm", src: "/eagle.png", alt: "", style: { position: "absolute", top: "60px", left: "50%", transform: "translateX(-50%)", width: "620px", filter: "var(--logo-filter)", opacity: "var(--wm-opacity)", pointerEvents: "none" } }),
      h("div", { key: "in", style: { position: "relative", textAlign: "center" } }, [
        h("div", { key: "meta", style: { display: "grid", gridTemplateColumns: "1fr auto 1fr", alignItems: "baseline", font: "600 10.5px 'IBM Plex Mono',monospace", textTransform: "uppercase", letterSpacing: ".16em", color: "var(--muted)", marginBottom: "16px" } }, [
          ed(head, "volume", "span", { justifySelf: "start", textAlign: "left" }, "v"),
          ed(head, "dispatch", "span", { justifySelf: "center", textAlign: "center" }, "d"),
          ed(head, "date", "span", { justifySelf: "end", textAlign: "right" }, "dt"),
        ]),
        h("img", { key: "e", src: "/eagle.png", alt: "Imperium", style: { height: "64px", filter: "var(--logo-filter)", opacity: 0.94, marginBottom: "8px" } }),
        ed(head, "title", "div", { fontFamily: "'Cinzel',serif", fontWeight: 900, fontSize: "72px", lineHeight: 0.88, letterSpacing: ".02em", color: "var(--ink)" }, "t"),
        ed(head, "tagline", "div", { font: "italic 500 16px/1 'Spectral',serif", color: "var(--gold)", marginTop: "12px", letterSpacing: ".04em" }, "tg"),
      ]),
      h("div", { key: "strip", style: { position: "relative", borderTop: "3px double var(--rule)", borderBottom: "3px double var(--rule)", marginTop: "20px", padding: "12px 0", textAlign: "center" } }, ed(head, "strip", "div", { font: "600 11px 'Zilla Slab',sans-serif", textTransform: "uppercase", letterSpacing: ".12em", color: "var(--muted)" }, "st")),
    ]);

  const palette = () =>
    h("div", { className: "vox-palette", style: { maxWidth: "960px", margin: "0 auto", padding: "10px 20px 14px", display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" } }, [
      h("span", { key: "l", style: { font: "700 10px 'IBM Plex Mono',monospace", textTransform: "uppercase", letterSpacing: ".1em", color: "var(--muted)" } }, "Add block:"),
      ...PALETTE.map((p) => h("button", { key: p.label, className: "addbtn", onClick: () => add(p.type, p.data) }, p.label)),
    ]);

  const blockNodes = blocks.map((b, i) =>
    h(
      "section",
      {
        key: b.id,
        id: b.id,
        className: "vox-block",
        style: { position: "relative", scrollMarginTop: "110px", padding: "32px 56px", borderTop: "1px solid var(--rule2)", background: i % 2 ? "var(--paper2)" : "var(--paper)" },
      },
      [
        edit
          ? h("div", { key: "ctl", className: "vox-blockctl", style: { position: "absolute", top: "10px", right: "10px", display: "flex", gap: "5px", zIndex: 5 } }, [
              h("button", { key: "u", className: "ctlbtn", title: "Move up", onClick: () => move(i, -1) }, "↑"),
              h("button", { key: "d", className: "ctlbtn", title: "Move down", onClick: () => move(i, 1) }, "↓"),
              h("button", { key: "c", className: "ctlbtn", title: "Duplicate", onClick: () => dup(i) }, "⧉"),
              h("button", { key: "x", className: "ctlbtn", title: "Delete", onClick: () => del(i) }, "✕"),
            ])
          : null,
        renderBlock(b),
      ],
    ),
  );

  const emptyHint =
    edit && blocks.length === 0
      ? h("div", { key: "empty", style: { padding: "60px 56px", textAlign: "center", font: "500 13px 'IBM Plex Mono',monospace", color: "var(--muted)" } }, "Empty — add a block above to begin.")
      : null;

  return h("div", { "data-theme": theme, className: edit ? "is-edit" : "", style: { color: "var(--ink)", fontFamily: "'Spectral',Georgia,serif" } }, [
    edit ? h("div", { key: "pal", style: { background: "var(--paper)", borderBottom: "1px solid var(--rule2)" } }, palette()) : null,
    h(
      "div",
      { key: "page", className: "vox-page", style: { maxWidth: "960px", margin: "0 auto", background: "var(--paper)", borderLeft: "1px solid var(--rule2)", borderRight: "1px solid var(--rule2)" } },
      [
        variant === "newsletter" ? masthead() : null,
        variant === "newsletter" ? toc() : null,
        ...blockNodes,
        emptyHint,
        variant === "newsletter"
          ? h("footer", { key: "ft", style: { borderTop: "3px double var(--rule)", padding: "28px 56px 40px", textAlign: "center" } }, [
              h("img", { key: "b", src: "/bee.png", alt: "Goon", style: { width: "76px", marginBottom: "12px" } }),
              h("div", { key: "t", style: { fontFamily: "'Cinzel',serif", fontWeight: 700, fontSize: "17px", letterSpacing: ".08em", color: "var(--ink)" } }, "VOX IMPERIALIS"),
            ])
          : null,
      ],
    ),
  ]);
}
